import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Search, RefreshCw, Trash2, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { STATUS_LABELS, STATUS_STYLES, fmtEur, buildClientReminderUrl } from "@/lib/quote";
import { QuoteDetail } from "@/components/admin/QuoteDetail";
import { WeekAhead } from "@/components/admin/WeekAhead";

const FILTERS = ["tutti", "nuovo", "in_revisione", "approvato", "acconto_ricevuto", "confermato", "completato", "rifiutato"];

export const QuotesBoard = () => {
  const [quotes, setQuotes] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("tutti");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/quotes", { params: { status: filter, search: search || undefined } });
      setQuotes(r.data);
      const all = filter === "tutti" && !search ? r.data : (await api.get("/admin/quotes", { params: { status: "tutti" } })).data;
      setAllQuotes(all);
      const c = { tutti: all.length };
      all.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
      setCounts(c);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const removeQuote = async (e, q) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare definitivamente il preventivo #${q.number} di ${q.contact.name}?`)) return;
    try {
      await api.delete(`/admin/quotes/${q.id}`);
      toast.success(`Preventivo #${q.number} eliminato`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div>
      <WeekAhead quotes={allQuotes} onSelect={setSelected} />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="quotes-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca cliente, evento, data…"
            className="pl-9 bg-[#1E293B] border-slate-700 text-white placeholder:text-slate-500" />
        </div>
        <button data-testid="quotes-refresh" onClick={load} className="p-2 text-slate-400 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${filter === f ? "bg-[#C85A32] text-white border-[#C85A32]" : "border-slate-700 text-slate-400 hover:text-white"}`}>
            {f === "tutti" ? "Tutti" : STATUS_LABELS[f]}{counts[f] ? ` · ${counts[f]}` : ""}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {quotes.map((q) => (
          <div key={q.id} data-testid={`quote-row-${q.id}`} onClick={() => setSelected(q)} role="button" tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSelected(q)}
            className="w-full bg-[#1E293B] border border-slate-700 rounded-xl p-4 text-left hover:border-[#C85A32] transition-colors cursor-pointer">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-sm">#{q.number} · {q.contact.name}</div>
                <div className="text-xs text-slate-400">{q.event.type}{q.event.type_custom ? ` (${q.event.type_custom})` : ""} · {q.event.date} · {q.guests.adults + (q.guests.teens || 0) + q.guests.children} ospiti</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono-num font-bold text-[#F0B429]">{fmtEur(q.totals.total)}</span>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${STATUS_STYLES[q.status]}`}>{STATUS_LABELS[q.status]}</span>
                <button data-testid={`quote-whatsapp-${q.id}`} onClick={(e) => { e.stopPropagation(); window.open(buildClientReminderUrl(q), "_blank"); }} title="Promemoria WhatsApp al cliente"
                  className="p-1.5 text-slate-500 hover:text-[#25D366] transition-colors">
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button data-testid={`quote-delete-${q.id}`} onClick={(e) => removeQuote(e, q)} title="Elimina preventivo"
                  className="p-1.5 text-slate-500 hover:text-rose-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {quotes.length === 0 && <p className="text-sm text-slate-500 text-center py-10">Nessun preventivo trovato</p>}
      </div>

      {selected && <QuoteDetail quoteId={selected.id} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
};
